<template>
  <div class="group-details-container my-3">
    <!-- Top Header Bar -->
    <div class="card border-0 shadow-sm rounded-3 p-3 mb-3 bg-white">
      <div class="d-flex flex-wrap align-items-center justify-content-between gap-3">
        <!-- Title & Subtitle -->
        <div class="d-flex align-items-center gap-3">
          <div class="icon-box-compact">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <div>
            <h1 class="h5 font-weight-bold mb-0 text-dark">Csoport Részletei & Tagjai</h1>
            <span class="text-muted small">Csoport beállítások, oktatók és beosztott táncosok</span>
          </div>
        </div>

        <!-- Group Selector Dropdown -->
        <div class="d-flex align-items-center gap-2">
          <label for="groupSelect" class="form-label mb-0 font-weight-bold text-muted small text-uppercase me-1">Csoport:</label>
          <select id="groupSelect" v-model="selectedGroupId" @change="onGroupChange" class="form-select form-select-sm custom-group-dropdown shadow-sm">
            <option v-for="g in allGroups" :key="g.id" :value="g.id">
              {{ g.name }} ({{ g.def_name }})
            </option>
          </select>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border text-primary spinner-border-sm me-2" role="status"></div>
      <span class="text-muted font-weight-bold">Betöltés...</span>
    </div>

    <!-- Main Content -->
    <div v-else-if="details" class="details-content">
      <!-- Compact Group Summary Strip -->
      <div class="card border-0 shadow-sm rounded-3 p-3 mb-3 bg-white">
        <div class="row g-3 align-items-center text-center text-md-start">
          <!-- Name & Def Name -->
          <div class="col-md-4 border-end-md">
            <span class="text-muted text-uppercase font-weight-bold me-2" style="font-size: 0.75rem;">Csoport:</span>
            <span class="font-weight-bold text-primary me-2" style="font-size: 1.05rem;">{{ details.group.name }}</span>
            <span class="badge bg-secondary-subtle text-secondary border px-2 py-0.5" style="font-size: 0.75rem;">{{ details.group.def_name }}</span>
          </div>

          <!-- Schedule -->
          <div class="col-md-5 border-end-md">
            <span class="text-muted text-uppercase font-weight-bold me-2" style="font-size: 0.75rem;">Órarend:</span>
            <span class="font-weight-bold text-dark d-inline-flex align-items-center gap-1.5" style="font-size: 0.875rem;">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-secondary">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              {{ parseSchedule(details.group.schedule) }}
            </span>
          </div>

          <!-- Monthly Fee -->
          <div class="col-md-3 text-md-end">
            <span class="text-muted text-uppercase font-weight-bold me-2" style="font-size: 0.75rem;">Havi díj:</span>
            <span class="badge bg-success-subtle text-success font-weight-bold border border-success-subtle px-2.5 py-1" style="font-size: 0.9rem;">
              {{ details.group.monthly_price ? formatHuf(details.group.monthly_price) : 'Nincs megadva' }}
            </span>
          </div>
        </div>
      </div>

      <div class="row g-3">
        <!-- Teachers Sidebar (Compact 4 cols) -->
        <div class="col-lg-4">
          <div class="card border-0 shadow-sm rounded-3 h-100 bg-white">
            <div class="card-header bg-transparent border-bottom py-2.5 px-3 d-flex align-items-center justify-content-between">
              <h2 class="h6 mb-0 font-weight-bold text-dark d-flex align-items-center">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="me-1.5 text-primary">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
                Oktatók
              </h2>
              <span class="badge bg-primary text-white rounded-pill px-2 py-0.5 font-weight-bold" style="font-size: 0.75rem;">
                {{ details.teachers ? details.teachers.length : 0 }} fő
              </span>
            </div>
            <div class="card-body p-2">
              <div v-if="details.teachers && details.teachers.length > 0" class="d-flex flex-column gap-1.5">
                <div v-for="t in details.teachers" :key="t.id" class="p-2.5 rounded-2 bg-light d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center gap-2.5">
                    <div class="avatar-circle-sm">
                      {{ getInitials(t.name) }}
                    </div>
                    <div>
                      <div class="font-weight-bold text-dark d-flex align-items-center gap-1.5" style="font-size: 0.85rem;">
                        {{ t.name }}
                        <span v-if="t.role === 900 || t.role === 999" class="badge bg-danger text-white rounded-pill px-1.5 py-0.2" style="font-size: 0.65rem;">Admin</span>
                        <span v-else class="badge bg-primary text-white rounded-pill px-1.5 py-0.2" style="font-size: 0.65rem;">Tanár</span>
                      </div>
                      <small class="text-muted d-block" style="font-size: 0.75rem;">{{ t.email }}</small>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="text-center py-4 text-muted small">
                Nincs oktató hozzárendelve.
              </div>
            </div>
          </div>
        </div>

        <!-- Dancers Main Table (Featured 8 cols) -->
        <div class="col-lg-8">
          <div class="card border-0 shadow-sm rounded-3 h-100 bg-white overflow-hidden">
            <div class="card-header bg-transparent border-bottom py-2.5 px-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div class="d-flex align-items-center gap-2">
                <h2 class="h6 mb-0 font-weight-bold text-dark d-flex align-items-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="me-1.5 text-success">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                  </svg>
                  Táncosok Listája
                </h2>
                <span class="badge bg-success text-white rounded-pill px-2.5 py-0.5 font-weight-bold" style="font-size: 0.75rem;">
                  {{ details.dancers ? details.dancers.length : 0 }} fő
                </span>
              </div>
              <div class="search-box">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="search-box__icon">
                  <circle cx="11" cy="11" r="8"/>
                  <path d="M21 21l-4.35-4.35"/>
                </svg>
                <input 
                  type="text" 
                  v-model="dancerFilter" 
                  class="form-control form-control-sm search-box__input" 
                  placeholder="Keresés név alapján..." 
                />
              </div>
            </div>
            <div class="card-body p-0">
              <div v-if="filteredDancers.length > 0" class="table-responsive">
                <table class="table align-middle mb-0 compact-dancers-table">
                  <thead>
                    <tr>
                      <th class="ps-3">Táncos neve</th>
                      <th>Szülő Email</th>
                      <th>Státusz</th>
                      <th class="text-end pe-3">Művelet</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="d in filteredDancers" :key="d.id" class="table-row-hover">
                      <td class="ps-3">
                        <div class="d-flex align-items-center gap-2">
                          <div class="dancer-avatar-sm">
                            {{ getInitials(d.name) }}
                          </div>
                          <span class="font-weight-bold text-dark" style="font-size: 0.875rem;">{{ d.name }}</span>
                        </div>
                      </td>
                      <td class="text-muted" style="font-size: 0.8rem;">{{ d.parent_email }}</td>
                      <td>
                        <span class="badge rounded-pill px-2 py-0.5" style="font-size: 0.72rem;" :class="d.status === 'Active' || d.status === 'active' ? 'bg-success-subtle text-success border border-success-subtle' : 'bg-secondary-subtle text-secondary'">
                          {{ d.status === 'Active' || d.status === 'active' ? 'Aktív' : d.status }}
                        </span>
                      </td>
                      <td class="text-end pe-3">
                        <router-link :to="'/children/' + d.id" class="btn btn-xs btn-outline-primary rounded-pill px-2.5 py-1 font-weight-bold" style="font-size: 0.75rem;">
                          Profil &rarr;
                        </router-link>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="text-center py-5 text-muted small">
                {{ dancerFilter ? 'Nincs a keresésnek megfelelő táncos.' : 'Nincs táncos ebben a csoportban.' }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'GroupDetailsItem',
  data() {
    return {
      selectedGroupId: null,
      allGroups: [],
      details: null,
      loading: true,
      dancerFilter: ''
    };
  },
  computed: {
    filteredDancers() {
      if (!this.details || !this.details.dancers) return [];
      if (!this.dancerFilter.trim()) return this.details.dancers;
      const q = this.dancerFilter.toLowerCase().trim();
      return this.details.dancers.filter(d => d.name && d.name.toLowerCase().includes(q));
    }
  },
  created() {
    this.init();
  },
  watch: {
    '$route.params.id'(newId) {
      if (newId) {
        this.selectedGroupId = parseInt(newId);
        this.loadGroupDetails(this.selectedGroupId);
      }
    }
  },
  methods: {
    async init() {
      await this.loadAllGroups();
      const routeId = this.$route.params.id;
      if (routeId) {
        this.selectedGroupId = parseInt(routeId);
      } else if (this.allGroups.length > 0) {
        this.selectedGroupId = this.allGroups[0].id;
      }
      if (this.selectedGroupId) {
        await this.loadGroupDetails(this.selectedGroupId);
      } else {
        this.loading = false;
      }
    },
    async loadAllGroups() {
      try {
        const r = await axios.get(this.$BACKEND_HOST + '/api/settings/groups/info');
        this.allGroups = r.data.groups || [];
      } catch (e) {
        console.error('Error loading groups list:', e);
      }
    },
    async loadGroupDetails(groupId) {
      if (!groupId) return;
      this.loading = true;
      try {
        const r = await axios.get(`${this.$BACKEND_HOST}/api/groups/${groupId}/details`);
        this.details = r.data;
      } catch (e) {
        console.error('Error loading group details:', e);
      } finally {
        this.loading = false;
      }
    },
    onGroupChange() {
      if (this.selectedGroupId) {
        this.$router.push(`/groups/${this.selectedGroupId}`).catch(() => {});
        this.loadGroupDetails(this.selectedGroupId);
      }
    },
    parseSchedule(schedStr) {
      if (!schedStr) return 'Nincs megadva';
      try {
        const list = JSON.parse(schedStr);
        if (Array.isArray(list)) {
          const daysMap = { "1": "Hétfő", "2": "Kedd", "3": "Szerda", "4": "Csütörtök", "5": "Péntek", "6": "Szombat", "7": "Vasárnap" };
          return list.map(s => {
            const dayStr = daysMap[s.day] || s.day;
            const freqStr = s.frequency === 'biweekly' 
              ? ` (kéthetente${s.start_date ? ', kezdve: ' + s.start_date : ''})` 
              : '';
            return `${dayStr} ${s.time}${freqStr}`;
          }).join(', ');
        }
      } catch (e) {}
      return schedStr;
    },
    formatHuf(val) {
      if (!val) return '0 Ft';
      return Math.round(val).toLocaleString('hu-HU') + ' Ft';
    },
    getInitials(name) {
      if (!name) return '?';
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.slice(0, 2).toUpperCase();
    }
  }
};
</script>

<style scoped>
.icon-box-compact {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--primary-blue, #2563eb) 0%, var(--secondary-blue, #1d4ed8) 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.custom-group-dropdown {
  border-radius: 10px;
  border: 1px solid #cbd5e1;
  background-color: #f8fafc;
  padding: 0.4rem 2.2rem 0.4rem 0.9rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #0f172a;
  transition: all 0.15s ease-in-out;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;
}

.custom-group-dropdown:hover {
  border-color: #94a3b8;
  background-color: #ffffff;
}

.custom-group-dropdown:focus {
  border-color: #2563eb;
  background-color: #ffffff;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15) !important;
  outline: none;
}

.avatar-circle-sm {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: white;
  font-weight: 700;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.dancer-avatar-sm {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e2e8f0;
  color: #334155;
  font-weight: 700;
  font-size: 0.7rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.search-box {
  position: relative;
  width: 200px;
}

.search-box__icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  pointer-events: none;
}

.search-box__input {
  padding-left: 28px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  font-size: 0.8rem;
}

.compact-dancers-table thead th {
  background-color: #f8fafc;
  font-size: 0.725rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
  font-weight: 700;
  padding: 0.6rem 0.75rem;
  border-bottom: 1px solid #e2e8f0;
}

.compact-dancers-table tbody td {
  padding: 0.55rem 0.75rem;
  border-bottom: 1px solid #f1f5f9;
}

.table-row-hover {
  transition: background-color 0.12s ease;
}
.table-row-hover:hover {
  background-color: #f8fafc;
}

.bg-primary-subtle { background-color: #eff6ff; }
.bg-secondary-subtle { background-color: #f1f5f9; }
.bg-amber-subtle { background-color: #fffbeb; }
.text-amber { color: #d97706; }
.bg-success-subtle { background-color: #f0fdf4; }

@media (min-width: 768px) {
  .border-end-md {
    border-right: 1px solid #e2e8f0;
  }
}
</style>
